/* Ayudate · Login único del panel
   Un solo usuario+contraseña para tutores y estudiantes: el servidor
   (iniciarSesionUnificado, apps-script.gs sección 9b) prueba primero
   contra "Credenciales" y después contra "CredencialesEstudiante", y
   devuelve con cuál rol coincidió — acá solo se guarda la sesión que
   corresponda y se manda a su panel. */

import React, { useState } from "react";
import { Marca } from "../componentes/Marca";
import { ESTILOS_MINIMOS } from "../estilos/minimos";
import { api, ErrorApi } from "../api";
import { irA_ruta } from "../lib/rutas";
import { guardarSesionTutor } from "../lib/sesionTutor";
import { guardarSesionEstudiante } from "../lib/sesionEstudiante";
import { Capa } from "../componentes/Capa";
import { CampoPassword } from "../componentes/CampoPassword";

export function PaginaLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [recuperando, setRecuperando] = useState(false);

  const enviar = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return setError("Complete usuario y contraseña.");

    setEnviando(true); setError("");
    try {
      const r = await api.iniciarSesion(username.trim(), password);
      if (r.rol === "tutor") {
        guardarSesionTutor(r.token, r.idTutor);
        irA_ruta("/paneltutor");
      } else {
        guardarSesionEstudiante(r.token, r.correo);
        irA_ruta("/panelestudiante");
      }
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      setError(codigo === "CREDENCIALES_INVALIDAS"
        ? "Usuario o contraseña incorrectos."
        : err.message || "No pudimos iniciar sesión. Pruebe de nuevo.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="v2">
      <style>{ESTILOS_MINIMOS}</style>
      <div className="mini-wrap">
        <div className="logo" style={{ marginBottom: 28 }}><Marca /></div>

        <form className="mini-card" onSubmit={enviar}>
          <h1>Iniciar sesión</h1>
          <p>Entre con su usuario y contraseña — <b>tutores y estudiantes usan el mismo acceso</b>,
             y lo llevamos directo a su panel.</p>

          <label className="lb" htmlFor="username">Usuario</label>
          <input id="username" className="in" value={username} autoComplete="username"
            onChange={(e) => setUsername(e.target.value)} placeholder="Su usuario" />

          <label className="lb" htmlFor="password">Contraseña</label>
          <CampoPassword id="password" className="in" value={password}
            autoComplete="current-password" onChange={(e) => setPassword(e.target.value)}
            placeholder="Su contraseña" />

          <button type="button" onClick={() => setRecuperando(true)} style={{
            display: "block", background: "none", border: "none", padding: 0, margin: "10px auto 0",
            color: "var(--text-2)", fontSize: 14, textDecoration: "underline",
            textUnderlineOffset: "3px", cursor: "pointer",
          }}>
            Olvidé mi contraseña
          </button>

          {error && <div className="error" role="alert">{error}</div>}

          <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }} disabled={enviando}>
            {enviando ? "Entrando…" : "Entrar"}
          </button>

          <a className="btn" href="/" style={{ width: "100%", textAlign: "center", marginTop: 10 }}
            onClick={(e) => { e.preventDefault(); irA_ruta("/"); }}>
            Volver al inicio
          </a>
        </form>
      </div>

      {recuperando && <RecuperarPassword onCerrado={() => setRecuperando(false)} />}
    </div>
  );
}

/* "Olvidé mi contraseña": no sabemos de antemano si quien escribe es tutor
   o estudiante, así que se pide la recuperación en las dos hojas a la vez
   — cada una ya contesta con el mismo mensaje genérico exista o no ese
   correo (solicitarRecuperacionPassword/Estudiante en apps-script.gs), así
   que mostrar la respuesta de cualquiera de las dos (o de ninguna, si las
   dos fallan de red) es igual de seguro y no hace falta esperar la más
   lenta: alcanza con que una termine. */
function RecuperarPassword({ onCerrado }) {
  const [email, setEmail] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState("");

  const enviar = async (e) => {
    e.preventDefault();
    if (!email.trim()) return setError("Ingrese su correo.");

    setEnviando(true); setError("");
    try {
      await Promise.any([
        api.solicitarRecuperacionPassword(email.trim()),
        api.solicitarRecuperacionPasswordEstudiante(email.trim()),
      ]);
      setEnviado(true);
    } catch (err) {
      setError("No pudimos procesar la solicitud. Pruebe de nuevo.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Capa onCerrar={onCerrado} etiqueta="Recuperar contraseña" confirmarSalida={!!email.trim() && !enviado}>
      <div className="mdl">
        <div className="mdl-h">
          <h3>Recuperar contraseña</h3>
          <button className="x" aria-label="Cerrar" onClick={onCerrado}>×</button>
        </div>
        <div className="mdl-b">
          {enviado ? (
            <>
              <p>Si el correo está registrado, va a recibir un enlace de recuperación en unos minutos.
                 <b> Revise su bandeja de entrada</b> (y spam).</p>
              <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }} onClick={onCerrado}>
                Entendido
              </button>
            </>
          ) : (
            <form onSubmit={enviar}>
              <p style={{ marginBottom: 16 }}>
                <b>Ingrese el correo con el que se registró.</b> Le vamos a mandar un enlace
                para elegir una contraseña nueva.
              </p>

              <label className="lb" htmlFor="email-recuperar">Correo electrónico</label>
              <input id="email-recuperar" className="in" type="email" value={email}
                autoComplete="email" onChange={(e) => setEmail(e.target.value)}
                placeholder="correo@ejemplo.com" />

              {error && <div className="error" role="alert">{error}</div>}

              <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }} disabled={enviando}>
                {enviando ? "Enviando…" : "Enviar enlace de recuperación"}
              </button>
            </form>
          )}
        </div>
      </div>
    </Capa>
  );
}
